import React, { useContext } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { SelectOption } from '@/shared/types';
import { ConditionView, CountryDatalist, FormTextInput, Grid, StepControlPanel } from '@/shared/ui';

import { ExtractFormType } from '@/modules/iac/pages/services/ExtendedCriminalRecord/lib/constants';
import { ExtendedCriminalRecordExtractSchema } from '@/modules/iac/pages/services/ExtendedCriminalRecord/model/types';

import AuthContext from '@/contexts/Auth/AuthContext';

import { AddressType } from '@/api/types';

import { RegistrationPlace } from '../registrationPlace/RegistrationPlace';

import './BirthPlaceAndRegistrationStep.scss';

export const BirthPlaceAndRegistrationStep: React.FC<StepProps> = ({ next, prev }) => {
  const { control } = useFormContext<ExtendedCriminalRecordExtractSchema>();
  const { addressesData } = useContext(AuthContext);
  const defaultBirthAddress = addressesData[AddressType.BIRTH];
  const defaultRegistrationAddress = addressesData[AddressType.REGISTRATION];
  const extractFormType = useWatch({
    control,
    name: 'extractFormType',
  });

  return (
    <>
      <FormBlock title="Місце народження">
        <div className="birth-place-and-registration-step__section">
          <FormTextInput
            name="birthCounttry"
            control={control}
            label="Країна"
            required
            defaultValue={defaultBirthAddress.country ?? undefined}
            list="birthCountry"
          />
          <CountryDatalist id="birthCountry" />
          <FormTextInput
            name="birthRegion"
            control={control}
            label="Область"
            defaultValue={defaultBirthAddress.region ?? undefined}
          />
          <FormTextInput
            name="birthDistrict"
            control={control}
            label="Район"
            defaultValue={defaultBirthAddress.district ?? undefined}
          />
          <FormTextInput
            name="birthCity"
            control={control}
            label="Населений пункт"
            required
            defaultValue={defaultBirthAddress.settlement ?? undefined}
          />
        </div>
      </FormBlock>
      <ConditionView if={extractFormType === ExtractFormType.ELECTRONIC}>
        <RegistrationPlace defaultRegistrationAddress={defaultRegistrationAddress} />
      </ConditionView>
      <StepControlPanel next={next} prev={prev} />
    </>
  );
};

const getDisplayValue = (value: Nullable<SelectOption | string>): Nullable<string> => {
  if (!value) {
    return null;
  }

  return typeof value === 'string' ? value : value.label;
};

export const BirthPlaceAndRegistrationPreview: React.FC = () => {
  const { getValues } = useFormContext<ExtendedCriminalRecordExtractSchema>();
  const {
    extractFormType,
    birthCounttry,
    birthRegion,
    birthDistrict,
    birthCity,
    registeredCountry,
    registeredRegion,
    registeredDistrict,
    registeredCity,
  } = getValues();

  const birthPlaceRows: Array<[string, Nullable<string>]> = [
    ['Країна', getDisplayValue(birthCounttry)],
    ['Область', getDisplayValue(birthRegion)],
    ['Район', getDisplayValue(birthDistrict)],
    ['Населений пункт', getDisplayValue(birthCity)],
  ];

  const registrationRows: Array<[string, Nullable<string>]> = [
    ['Країна', getDisplayValue(registeredCountry)],
    ['Область', getDisplayValue(registeredRegion)],
    ['Район', getDisplayValue(registeredDistrict)],
    ['Населений пункт', getDisplayValue(registeredCity)],
  ];

  return (
    <>
      <Grid title="Місце народження" data={birthPlaceRows} />
      <ConditionView if={extractFormType === ExtractFormType.ELECTRONIC}>
        <Grid title="Місце реєстрації" data={registrationRows} />
      </ConditionView>
    </>
  );
};
